import DateDisplay from '@app/src/components/DateDisplay'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import type { RiskReasonTranslation } from '@app/src/types/topicalRisk'
import { getRiskStatusShortKey } from '@app/src/utils/riskStatusHelpers'
import {
  TimelineItem as MuiTimelineItem,
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  timelineItemClasses,
  TimelineSeparator,
} from '@mui/lab'
import { Box, Typography, useTheme } from '@mui/material'
import * as React from 'react'
import { useIntl } from 'react-intl'

interface TimelineItem {
  createdAt: string
  riskStatus: RiskStatus
  riskStatusKey?: string
  descriptionTranslation?: RiskReasonTranslation | null
  isInitialCalculation?: boolean
}

/**
 * Returns the translation key for the timeline header based on the risk change context.
 */
function getHeaderTranslationKey(item: TimelineItem): string {
  if (item.isInitialCalculation) return 'topicalRisk.riskReason.header.calculated'
  if (!item.descriptionTranslation) return 'topicalRisk.riskReason.header.recalculated'
  return 'topicalRisk.riskReason.header.updated'
}

interface TopicalRiskProviderTimelineProps {
  items: TimelineItem[]
}

/**
 * Translates a risk reason description using formatMessage.
 * Handles fallback translation for missing names.
 */
function translateRiskReason(
  translation: RiskReasonTranslation,
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | string[]>) => string,
): string {
  const { messageKey, values } = translation

  // Process fallback keys: if value is a translation key, translate it
  const processedValues = Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      if (typeof value === 'string' && value.startsWith('topicalRisk.riskReason.fallback.')) {
        return [key, formatMessage({ id: value })]
      }
      return [key, value]
    }),
  )

  return formatMessage({ id: messageKey }, processedValues)
}

const TopicalRiskProviderTimeline = ({ items }: TopicalRiskProviderTimelineProps) => {
  const { typography } = useTheme()
  const { formatMessage } = useIntl()

  return (
    <Timeline
      sx={{
        m: 0,
        py: 0,
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {items.map((item, index) => {
        const isFirst = index === 0
        const isLast = index === items.length - 1

        // Translate description
        const translatedDescription = item.descriptionTranslation
          ? translateRiskReason(item.descriptionTranslation, formatMessage)
          : null

        const headerKey = getHeaderTranslationKey(item)

        return (
          <MuiTimelineItem key={index} sx={{ width: '100%' }}>
            <TimelineSeparator>
              <TimelineDot color="primary" sx={{ boxShadow: 'none' }} variant={isFirst ? 'filled' : 'outlined'} />
              {!isLast && <TimelineConnector color="primary" />}
            </TimelineSeparator>
            <TimelineContent sx={{ pb: isLast ? 0 : 3, mt: '-1px' }}>
              <Typography variant="caption" color="text.secondary">
                <DateDisplay value={item.createdAt} />
              </Typography>
              <Box>
                {formatMessage({ id: headerKey })}:{' '}
                <Typography fontWeight="600" component="span">
                  {formatMessage({ id: getRiskStatusShortKey(item.riskStatus) })}
                </Typography>
              </Box>
              {translatedDescription && (
                <Typography
                  color="text.secondary"
                  lineHeight={1.6}
                  fontSize={typography?.caption.fontSize}
                  sx={{ pt: 0.3 }}
                >
                  {translatedDescription}
                </Typography>
              )}
            </TimelineContent>
          </MuiTimelineItem>
        )
      })}
    </Timeline>
  )
}

export default TopicalRiskProviderTimeline
